import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, Check, AlertCircle, Lightbulb, HelpCircle, Download } from 'lucide-react';
import { helpCenterArticles, troubleshootingArticles } from '../data/knowledgeHub';
import { Helmet } from 'react-helmet-async';

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.typeit4u.keyboard";

const HelpArticle = () => {
  const { slug } = useParams();
  
  const article = [...helpCenterArticles, ...troubleshootingArticles].find(a => a.slug === slug);
  
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 container text-center">
          <h1 className="text-3xl font-bold mb-4">Article Not Found</h1>
          <Link to="/help" className="text-orange-600 hover:underline">Back to Help Center</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const isTroubleshooting = 'problem' in article;

  // Generate FAQ Schema
  const faqSchema = article.faq ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faq.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  } : null;

  // Generate HowTo Schema
  const howToSchema = article.steps ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": article.title,
    "description": article.description,
    "step": article.steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.title,
      "text": step.content
    }))
  } : null;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{article.title} | TypeIt4U Help</title>
        <meta name="description" content={article.description} />
        <meta name="keywords" content={article.keywords?.join(', ')} />
        <link rel="canonical" href={`https://typeit4u.com/help/${article.slug}`} />
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
        {howToSchema && <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>}
      </Helmet>

      <Navbar />
      
      {/* Breadcrumb */}
      <section className="pt-28 pb-4 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <Link to="/help" className="inline-flex items-center gap-2 text-gray-600 hover:text-orange-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Help Center
          </Link>
        </div>
      </section>

      {/* Article Content */}
      <section className="pb-20 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container max-w-3xl">
          <motion.article
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
          >
            {/* Header */}
            <div className="mb-8">
              <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-4">
                {article.category}
              </span>
              <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
                {article.title}
              </h1>
              <p className="text-lg text-gray-600">{article.description}</p>
            </div>

            {/* Problem Statement (for troubleshooting) */}
            {isTroubleshooting && article.problem && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-8">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h2 className="font-semibold text-red-800 mb-1">Problem</h2>
                    <p className="text-red-700">{article.problem}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Steps */}
            {article.steps && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                <h2 className="text-xl font-semibold mb-6" style={{ color: '#0A0A0A' }}>
                  {isTroubleshooting ? 'How to Fix' : 'Step-by-Step Guide'}
                </h2>
                <div className="space-y-4">
                  {article.steps.map((step, index) => (
                    <div key={index} className="flex gap-4">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold text-sm"
                        style={{ background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)' }}
                      >
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{step.title}</h3>
                        <p className="text-gray-600">{step.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Solutions (for troubleshooting) */}
            {article.solutions && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                <h2 className="text-xl font-semibold mb-6" style={{ color: '#0A0A0A' }}>Solutions</h2>
                <div className="space-y-4">
                  {article.solutions.map((solution, index) => (
                    <div key={index} className="flex gap-4">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, rgba(255, 140, 66, 0.1) 0%, rgba(255, 107, 157, 0.1) 100%)' }}
                      >
                        <Check className="w-4 h-4" style={{ color: '#FF6B9D' }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{solution.title}</h3>
                        <p className="text-gray-600">{solution.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tips */}
            {article.tips && article.tips.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-5 mb-8">
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h2 className="font-semibold text-orange-800 mb-2">Tips</h2>
                    <ul className="space-y-1">
                      {article.tips.map((tip, index) => (
                        <li key={index} className="text-orange-700 flex items-start gap-2">
                          <span>•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* FAQ */}
            {article.faq && article.faq.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <HelpCircle className="w-5 h-5" style={{ color: '#FF6B9D' }} />
                  <h2 className="text-xl font-semibold" style={{ color: '#0A0A0A' }}>FAQ</h2>
                </div>
                <div className="space-y-4">
                  {article.faq.map((item, index) => (
                    <div key={index}>
                      <h3 className="font-semibold text-gray-900 mb-1">{item.q}</h3>
                      <p className="text-gray-600">{item.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-xl p-8 text-center border border-orange-100">
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0A0A0A' }}>
                Ready to Try TypeIt4U?
              </h3>
              <p className="text-gray-600 mb-6">
                Download now and experience India's smartest AI keyboard.
              </p>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                  color: 'white',
                  borderRadius: '26px',
                  padding: '14px 28px',
                  fontSize: '16px',
                  fontWeight: 500
                }}
              >
                <Download className="w-5 h-5" />
                Download TypeIt4U
              </a>
            </div>
          </motion.article>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HelpArticle;
